import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { analysisService } from '../../services/analysisService';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronRight,
  faHeading,
  faParagraph,
  faList,
  faTable,
  faQuoteRight,
  faFilter,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { SectionType } from '../../types';
import { useState, useEffect, useRef } from 'react';

const sectionIcons: Record<SectionType, any> = {
  HEADING_1: faHeading,
  HEADING_2: faHeading,
  HEADING_3: faHeading,
  PARAGRAPH: faParagraph,
  LIST_ITEM: faList,
  TABLE: faTable,
  QUOTE: faQuoteRight,
};

const sectionLabels: Record<SectionType, string> = {
  HEADING_1: 'Título Principal',
  HEADING_2: 'Subtítulo',
  HEADING_3: 'Encabezado',
  PARAGRAPH: 'Párrafo',
  LIST_ITEM: 'Elemento de Lista',
  TABLE: 'Tabla',
  QUOTE: 'Cita',
};

export default function SectionsList() {
  const navigate = useNavigate();
  const { analysisId } = useParams<{ analysisId: string }>();

  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'compliant' | 'non-compliant' | 'needs-glossary'>('all');
  const [auditFilter, setAuditFilter] = useState<'all' | 'audited' | 'unaudited'>('all');
  const [inputText, setInputText] = useState(''); // Text user is typing
  const [searchText, setSearchText] = useState(''); // Text used for actual search (debounced)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input - wait 2000ms after user stops typing
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setSearchText(inputText);
      setPage(1); // Reset to page 1 when search changes
    }, 2000);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputText]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['analysis-sections', analysisId, page, filter, auditFilter, searchText],
    queryFn: () => analysisService.getAnalysisSections(Number(analysisId), {
      page,
      limit: 100,
      filter: filter === 'all' ? undefined : filter,
      auditStatus: auditFilter === 'all' ? undefined : (auditFilter as 'audited' | 'unaudited'),
      search: searchText || undefined,
    }),
    enabled: !!analysisId,
  });

  // Helper function to check if a section needs glossary (for display purposes only)
  const checkNeedsGlossary = (section: any) => {
    const hasGlossaryTermsCount = section.sectionMetadata?.glossaryTerms?.count > 0;
    const hasGlossaryAnalysisCount = section.sectionMetadata?.glossaryAnalysis?.count > 0;
    const hasUniqueTerms = section.sectionMetadata?.glossaryTerms?.uniqueTerms?.length > 0 ||
                          section.sectionMetadata?.glossaryAnalysis?.uniqueTerms?.length > 0;
    const hasComplexWordsCount = section.complexWordsCount > 0;

    return hasGlossaryTermsCount || hasGlossaryAnalysisCount || hasUniqueTerms || hasComplexWordsCount;
  };

  // Reset page when filter or search changes
  const handleFilterChange = (newFilter: 'all' | 'compliant' | 'non-compliant' | 'needs-glossary') => {
    setFilter(newFilter);
    setPage(1);
  };

  const handleAuditFilterChange = (newFilter: 'all' | 'audited' | 'unaudited') => {
    setAuditFilter(newFilter);
    setPage(1);
  };

  const handleSearchChange = (text: string) => {
    setInputText(text); // Update input immediately for user feedback
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando secciones...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No se encontraron secciones</p>
      </div>
    );
  }

  const goBack = () => {
    // Always go back to the document/web analysis page based on source type
    // Document types: PDF, DOCX, TXT, TSV
    if (['PDF', 'DOCX', 'TXT', 'TSV'].includes(data.analysis.sourceType)) {
      navigate('/analysis/document');
    } else if (data.analysis.sourceType === 'URL') {
      navigate('/analysis/web');
    } else {
      // Fallback to history for MANUAL type
      navigate('/history');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-600 rounded-xl p-8 shadow-lg">
        <Button onClick={goBack} variant="outline" className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/30">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Volver
        </Button>
        <h1 className="text-3xl font-bold mb-2 text-white">{data.analysis.title}</h1>
        <div className="flex items-center space-x-4 text-white/90">
          <span>{data.total} secciones</span>
          <span>•</span>
          <span>{new Date(data.analysis.createdAt).toLocaleDateString('es-ES')}</span>
          {data.analysis.sector && (
            <>
              <span>•</span>
              <span>{data.analysis.sector.icon} {data.analysis.sector.name}</span>
            </>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por título o contenido..."
                value={inputText}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
              />
            </div>
            {inputText && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setInputText('');
                  setSearchText('');
                }}
              >
                Limpiar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filter Buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-700">
          Secciones del Documento ({data?.totalFiltered ?? data?.sections?.length ?? 0} de {data?.total ?? 0})
        </h3>
        <div className="flex items-center space-x-2">
          <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('all')}
          >
            Todas ({data.total})
          </Button>
          <Button
            variant={filter === 'compliant' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('compliant')}
            className={filter === 'compliant' ? '' : 'text-green-600 border-green-200 hover:bg-green-50'}
          >
            Cumplen B2
          </Button>
          <Button
            variant={filter === 'non-compliant' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('non-compliant')}
            className={filter === 'non-compliant' ? '' : 'text-amber-600 border-amber-200 hover:bg-amber-50'}
          >
            No cumplen B2
          </Button>
          <Button
            variant={filter === 'needs-glossary' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange('needs-glossary')}
            className={filter === 'needs-glossary' ? '' : 'text-orange-600 border-orange-200 hover:bg-orange-50'}
          >
            Requieren glosario
          </Button>
        </div>

        {/* Audit Status Filters - Independent Line */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-600">Estado de auditoría:</span>
          <Button
            variant={auditFilter === 'audited' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleAuditFilterChange('audited')}
            className={auditFilter === 'audited' ? '' : 'text-green-600 border-green-200 hover:bg-green-50'}
          >
            Auditado
          </Button>
          <Button
            variant={auditFilter === 'unaudited' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleAuditFilterChange('unaudited')}
            className={auditFilter === 'unaudited' ? '' : 'text-gray-600 border-gray-200 hover:bg-gray-50'}
          >
            Sin Auditar
          </Button>
        </div>
      </div>

      {/* Sections List */}
      <div className="space-y-4">
        <div className="grid gap-4">
          {data?.sections?.map((section, index) => (
            <Card
              key={section.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/section/${section.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <FontAwesomeIcon
                        icon={sectionIcons[section.sectionType]}
                        className="text-blue-600 text-xl"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">
                          {sectionLabels[section.sectionType]}
                        </span>
                        <span className="text-xs text-gray-500">Sección {index + 1}</span>
                        {/* Audit Status Badge */}
                        <Badge className={`flex items-center gap-1 text-xs ${
                          section.auditStatus === 'AUDITED'
                            ? 'bg-green-500/90 text-white border-green-400'
                            : 'bg-gray-400/90 text-white border-gray-300'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            section.auditStatus === 'AUDITED' ? 'bg-green-200' : 'bg-gray-200'
                          }`}></span>
                          {section.auditStatus === 'AUDITED' ? 'Auditado' : 'Sin Auditar'}
                        </Badge>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {section.title || `${sectionLabels[section.sectionType]} ${index + 1}`}
                      </h4>
                      <p className="text-xs text-gray-600 line-clamp-2 mb-3">
                        {section.content}
                      </p>

                      {/* Lenguaje Claro Compliance Summary */}
                      {section.sectionMetadata?.b2Criteria && (() => {
                        const allCriteria = section.sectionMetadata.b2Criteria.criteria || [];
                        const manualValidations = (section.sectionMetadata as any)?.manualValidations || {};

                        // Organize criteria by UNE principle (EXACT same filters as AnalysisResultPage)
                        const pertinenciaCriteria = allCriteria.filter((c: any) =>
                          c.name.toLowerCase().includes('adecuación al público') ||
                          c.name.toLowerCase().includes('propósito claro') ||
                          c.name.toLowerCase().includes('suficiencia') ||
                          c.name.toLowerCase().includes('superflua')
                        );

                        const facilidadBusquedaCriteria = allCriteria.filter((c: any) =>
                          (c.name.toLowerCase().includes('estructura clara') && c.name.toLowerCase().includes('títulos')) ||
                          c.name.toLowerCase().includes('listas') ||
                          c.name.toLowerCase().includes('viñetas') ||
                          c.name.toLowerCase().includes('ilustraciones') ||
                          (c.name.toLowerCase().includes('gráficos') && c.name.toLowerCase().includes('iconos'))
                        );

                        const comprensibilidadCriteria = allCriteria.filter((c: any) => {
                          if (c.name.toLowerCase().includes('listas') ||
                              c.name.toLowerCase().includes('viñetas') ||
                              c.name.toLowerCase().includes('ilustraciones') ||
                              c.name.toLowerCase().includes('gráficos') ||
                              (c.name.toLowerCase().includes('iconos') && c.name.toLowerCase().includes('glosario'))) {
                            return false;
                          }

                          return (
                            ['legibilidad', 'vocabulario', 'claridad'].includes(c.category?.toLowerCase()) ||
                            (c.category?.toLowerCase() === 'estructura' &&
                             (c.name.toLowerCase().includes('voz activa') ||
                              c.name.toLowerCase().includes('conectores') ||
                              c.name.toLowerCase().includes('subordinadas'))) ||
                            (c.category?.toLowerCase() === 'formato' && c.name.toLowerCase().includes('mayúsculas')) ||
                            c.name.toLowerCase().includes('inflesz') ||
                            c.name.toLowerCase().includes('glosario') ||
                            c.name.toLowerCase().includes('negaciones') ||
                            c.name.toLowerCase().includes('indeterminadas') ||
                            c.name.toLowerCase().includes('tono y estilo')
                          );
                        });

                        const usabilidadCriteria = allCriteria.filter((c: any) =>
                          c.name.toLowerCase().includes('validación') ||
                          c.name.toLowerCase().includes('usuarios reales') ||
                          c.name.toLowerCase().includes('orientación a la acción') ||
                          c.name.toLowerCase().includes('siguientes pasos')
                        );

                        // Add 3 manual Pertinencia criteria with fixed indices
                        const manualPertinenciaCriteria = [
                          {
                            name: 'Adecuación al Público Objetivo',
                            category: 'pertinencia',
                            description: 'Verificar que el documento se ha escrito pensando en el perfil del lector',
                            scientific: false,
                            status: null,
                            index: 0
                          },
                          {
                            name: 'Propósito Claro',
                            category: 'pertinencia',
                            description: 'Evaluar si el documento responde directamente al objetivo del lector',
                            scientific: false,
                            status: null,
                            index: 1
                          },
                          {
                            name: 'Suficiencia de la Información',
                            category: 'pertinencia',
                            description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo',
                            scientific: false,
                            status: null,
                            index: 2
                          }
                        ];

                        // Modified calculatePrincipleStatus to use LOCAL index (same as AnalysisResultPage)
                        const calculatePrincipleStatus = (criteria: any[], useWeights: boolean = false) => {
                          if (criteria.length === 0) return { passes: true, percentage: 100, validated: 0, total: 0 };

                          if (useWeights) {
                            let earnedScore = 0;
                            let maxScore = 0;

                            criteria.forEach((criterion, index) => {
                              // Use LOCAL index within this criteria array, NOT global index
                              const criterionKey = `${criterion.name}-${index}`;
                              const validation = manualValidations[criterionKey] !== undefined
                                ? manualValidations[criterionKey]
                                : (!criterion.scientific ? null : criterion.status === 'pass');

                              const weight = criterion.weight || 0;
                              maxScore += weight;

                              if (validation === true) {
                                earnedScore += weight;
                              }
                            });

                            const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
                            return {
                              passes: percentage >= 85,
                              percentage: percentage,
                              validated: Math.round(earnedScore),
                              total: maxScore
                            };
                          } else {
                            let passedCount = 0;
                            let totalCriteria = criteria.length;

                            criteria.forEach((criterion, index) => {
                              // Use LOCAL index within this criteria array, NOT global index
                              const criterionKey = `${criterion.name}-${index}`;
                              const validation = manualValidations[criterionKey] !== undefined
                                ? manualValidations[criterionKey]
                                : (!criterion.scientific ? null : criterion.status === 'pass');

                              if (validation === true) {
                                passedCount++;
                              }
                            });

                            const percentage = (passedCount / totalCriteria) * 100;
                            return {
                              passes: percentage >= 85,
                              percentage: percentage,
                              validated: passedCount,
                              total: totalCriteria
                            };
                          }
                        };

                        // Calculate principle statuses exactly as in AnalysisResultPage
                        const pertinenciaStatus = calculatePrincipleStatus([...manualPertinenciaCriteria, ...pertinenciaCriteria], false);
                        const facilidadStatus = facilidadBusquedaCriteria.length > 0
                          ? calculatePrincipleStatus(facilidadBusquedaCriteria, false)
                          : { passes: true, percentage: 100, validated: 0, total: 0 };
                        const comprensibilidadStatus = calculatePrincipleStatus(comprensibilidadCriteria, true);
                        const usabilidadStatus = usabilidadCriteria.length > 0
                          ? calculatePrincipleStatus(usabilidadCriteria, false)
                          : { passes: true, percentage: 100, validated: 0, total: 0 };

                        // Calculate what percentage would be achieved with glossary
                        let comprensibilidadWithGlossary = null;
                        if (checkNeedsGlossary(section) && !comprensibilidadStatus.passes) {
                          let earnedScoreWithGlossary = 0;
                          let maxScore = 0;

                          comprensibilidadCriteria.forEach((criterion: any, index: number) => {
                            const weight = criterion.weight || 1;
                            maxScore += weight;

                            const criterionKey = `${criterion.name}-${index}`;
                            const currentValidation = manualValidations[criterionKey] !== undefined
                              ? manualValidations[criterionKey]
                              : (!criterion.scientific ? null : criterion.status === 'pass');

                            // If it's a glossary criterion, assume it would pass with glossary
                            const isGlossaryCriterion = criterion.name.toLowerCase().includes('glosario');
                            const validation = isGlossaryCriterion ? true : currentValidation;

                            if (validation === true) {
                              earnedScoreWithGlossary += weight;
                            }
                          });

                          const percentageWithGlossary = maxScore > 0 ? (earnedScoreWithGlossary / maxScore) * 100 : 0;
                          comprensibilidadWithGlossary = {
                            percentage: percentageWithGlossary,
                            passes: percentageWithGlossary >= 85
                          };
                        }

                        const cumpleLenguajeClaro = pertinenciaStatus.passes && facilidadStatus.passes && comprensibilidadStatus.passes && usabilidadStatus.passes;

                        return (
                          <div className="space-y-3">
                            {/* First Row: Lenguaje Claro and Comprensibilidad B2 */}
                            <div className="flex items-center gap-4">
                              {/* Plain Language Compliance */}
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Lenguaje Claro:</span>
                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                  cumpleLenguajeClaro
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {cumpleLenguajeClaro ? '✓ Cumple' : '✗ No cumple'}
                                </span>
                              </div>

                              {/* Comprensibilidad B2 */}
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Comprensibilidad B2:</span>
                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                  comprensibilidadStatus.passes
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {comprensibilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({comprensibilidadStatus.percentage.toFixed(0)}%)
                                </span>
                              </div>

                              {/* Glosario Badge - Same Row */}
                              {checkNeedsGlossary(section) && (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-700">Glosario:</span>
                                  <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-800 text-sm font-bold">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-xs" />
                                    Requerido
                                    {comprensibilidadWithGlossary && (
                                      <span className="ml-1">
                                        (Con glosario: {comprensibilidadWithGlossary.percentage.toFixed(0)}%)
                                      </span>
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Second Row: Glossary Terms */}
                            {checkNeedsGlossary(section) && (() => {
                              const glossaryTerms = section.sectionMetadata?.glossaryTerms?.terms || [];

                              return glossaryTerms.length > 0 ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-gray-700">Términos que Requieren de Glosario:</span>
                                  {glossaryTerms.map((term: string, index: number) => (
                                    <span key={index} className="px-2 py-1 rounded-md bg-orange-100 text-orange-800 text-sm font-medium">
                                      {term}
                                    </span>
                                  ))}
                                </div>
                              ) : null;
                            })()}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="text-gray-400 text-xl"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Load More Button */}
        {data?.hasMore && (
          <div className="flex justify-center pt-6">
            <Button
              onClick={handleLoadMore}
              disabled={isFetching}
              size="lg"
              variant="outline"
              className="min-w-[200px]"
            >
              {isFetching ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2" />
                  Cargando...
                </>
              ) : (
                `Ver más (${data.totalFiltered - (data.sections?.length ?? 0)} restantes)`
              )}
            </Button>
          </div>
        )}

        {/* No results message */}
        {data && data.sections && data.sections.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No se encontraron secciones que coincidan con los filtros seleccionados</p>
          </div>
        )}
      </div>
    </div>
  );
}
